import { 
  ReportConfig, 
  ReportColumn, 
  ReportDataSource 
} from '../types';

export const DATA_SOURCE_LABELS: Record<ReportDataSource, string> = {
  'leads': 'Leads',
  'deals': 'Deals',
  'activities': 'Activities',
  'projects': 'Projects',
  'pipeline-analytics': 'Pipeline Analytics',
  'conversion-metrics': 'Conversion Metrics',
  'revenue-forecast': 'Revenue Forecast',
  'utm-campaigns': 'UTM Campaigns',
  'utm-sources': 'UTM Sources',
};

// Column definitions for each data source
export const COLUMN_DEFINITIONS: Record<ReportDataSource, ReportColumn[]> = {
  'leads': [
    { field: 'firstName', label: 'First Name', type: 'string', visible: true },
    { field: 'lastName', label: 'Last Name', type: 'string', visible: true },
    { field: 'email', label: 'Email', type: 'string', visible: true },
    { field: 'phone', label: 'Phone', type: 'string', visible: false },
    { field: 'company', label: 'Company', type: 'string', visible: false },
    { field: 'projectType', label: 'Project Type', type: 'string', visible: true },
    { field: 'budget', label: 'Budget', type: 'currency', visible: true },
    { field: 'location', label: 'Location', type: 'string', visible: true },
    { field: 'source', label: 'Source', type: 'string', visible: true },
    { field: 'status', label: 'Status', type: 'string', visible: true },
    { field: 'createdAt', label: 'Created Date', type: 'date', visible: true },
    { field: 'assignedTo', label: 'Assigned To', type: 'string', visible: false },
  ],
  'deals': [
    { field: 'title', label: 'Deal Title', type: 'string', visible: true },
    { field: 'contactName', label: 'Contact Name', type: 'string', visible: true },
    { field: 'value', label: 'Value', type: 'currency', visible: true },
    { field: 'stageId', label: 'Stage', type: 'string', visible: true },
    { field: 'probability', label: 'Probability', type: 'percentage', visible: true },
    { field: 'outcome', label: 'Outcome', type: 'string', visible: true },
    { field: 'projectType', label: 'Project Type', type: 'string', visible: false },
    { field: 'location', label: 'Location', type: 'string', visible: false },
    { field: 'source', label: 'Source', type: 'string', visible: false },
    { field: 'createdAt', label: 'Created Date', type: 'date', visible: true },
    { field: 'closedAt', label: 'Closed Date', type: 'date', visible: false },
    { field: 'daysInStage', label: 'Days in Stage', type: 'number', visible: false },
    { field: 'assignedTo', label: 'Assigned To', type: 'string', visible: false },
  ],
  'activities': [
    { field: 'title', label: 'Activity Title', type: 'string', visible: true },
    { field: 'type', label: 'Type', type: 'string', visible: true },
    { field: 'entityName', label: 'Related To', type: 'string', visible: true },
    { field: 'status', label: 'Status', type: 'string', visible: true },
    { field: 'scheduledAt', label: 'Scheduled', type: 'date', visible: true },
    { field: 'completedAt', label: 'Completed', type: 'date', visible: false },
    { field: 'duration', label: 'Duration (min)', type: 'number', visible: false },
    { field: 'assignedTo', label: 'Assigned To', type: 'string', visible: true },
    { field: 'createdAt', label: 'Created Date', type: 'date', visible: false },
    { field: 'outcome', label: 'Outcome', type: 'string', visible: false },
  ],
  'projects': [
    { field: 'name', label: 'Project Name', type: 'string', visible: true },
    { field: 'clientName', label: 'Client', type: 'string', visible: true },
    { field: 'status', label: 'Status', type: 'string', visible: true },
    { field: 'value', label: 'Value', type: 'currency', visible: true },
    { field: 'projectType', label: 'Project Type', type: 'string', visible: true },
    { field: 'location', label: 'Location', type: 'string', visible: false },
    { field: 'startDate', label: 'Start Date', type: 'date', visible: true },
    { field: 'targetEndDate', label: 'Target End', type: 'date', visible: true },
    { field: 'projectManager', label: 'Project Manager', type: 'string', visible: false },
  ],
  'pipeline-analytics': [
    { field: 'stageName', label: 'Stage', type: 'string', visible: true },
    { field: 'count', label: 'Deal Count', type: 'number', visible: true },
    { field: 'totalValue', label: 'Total Value', type: 'currency', visible: true },
    { field: 'avgValue', label: 'Avg Value', type: 'currency', visible: true },
    { field: 'avgTimeInStage', label: 'Avg Time (days)', type: 'number', visible: true },
    { field: 'conversionRate', label: 'Conversion Rate', type: 'percentage', visible: true },
  ],
  'conversion-metrics': [
    { field: 'source', label: 'Source', type: 'string', visible: true },
    { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
    { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
    { field: 'conversionRate', label: 'Lead→Deal Rate', type: 'percentage', visible: true },
    { field: 'wonCount', label: 'Won', type: 'number', visible: true },
    { field: 'winRate', label: 'Win Rate', type: 'percentage', visible: true },
    { field: 'totalValue', label: 'Total Value', type: 'currency', visible: true },
    { field: 'avgValue', label: 'Avg Deal Value', type: 'currency', visible: true },
  ],
  'revenue-forecast': [
    { field: 'month', label: 'Month', type: 'string', visible: true },
    { field: 'pipelineValue', label: 'Pipeline Value', type: 'currency', visible: true },
    { field: 'weightedValue', label: 'Weighted Value', type: 'currency', visible: true },
    { field: 'expectedCloses', label: 'Expected Closes', type: 'number', visible: true },
    { field: 'avgDealSize', label: 'Avg Deal Size', type: 'currency', visible: true },
  ],
  'utm-campaigns': [
    { field: 'campaign', label: 'Campaign', type: 'string', visible: true },
    { field: 'source', label: 'Source', type: 'string', visible: true },
    { field: 'medium', label: 'Medium', type: 'string', visible: true },
    { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
    { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
    { field: 'conversionRate', label: 'Conversion Rate', type: 'percentage', visible: true },
    { field: 'wonCount', label: 'Won', type: 'number', visible: true },
    { field: 'winRate', label: 'Win Rate', type: 'percentage', visible: true },
    { field: 'totalRevenue', label: 'Revenue', type: 'currency', visible: true },
    { field: 'avgDealValue', label: 'Avg Deal Value', type: 'currency', visible: true },
  ],
  'utm-sources': [
    { field: 'source', label: 'UTM Source', type: 'string', visible: true },
    { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
    { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
    { field: 'conversionRate', label: 'Lead→Deal Rate', type: 'percentage', visible: true },
    { field: 'wonCount', label: 'Won', type: 'number', visible: true },
    { field: 'winRate', label: 'Win Rate', type: 'percentage', visible: true },
    { field: 'totalRevenue', label: 'Total Revenue', type: 'currency', visible: true },
    { field: 'avgDealValue', label: 'Avg Deal Value', type: 'currency', visible: true },
  ],
};

export const getAvailableColumns = (dataSource: ReportDataSource): ReportColumn[] => {
  return COLUMN_DEFINITIONS[dataSource] || [];
};

// Pre-built report templates
export const REPORT_TEMPLATES: ReportConfig[] = [
  {
    name: 'Lead Source Analysis',
    description: 'Analyze lead performance by source channel',
    dataSource: 'leads',
    columns: [
      { field: 'source', label: 'Source', type: 'string', visible: true },
      { field: 'firstName', label: 'First Name', type: 'string', visible: true },
      { field: 'lastName', label: 'Last Name', type: 'string', visible: true },
      { field: 'projectType', label: 'Project Type', type: 'string', visible: true },
      { field: 'budget', label: 'Budget', type: 'currency', visible: true },
      { field: 'status', label: 'Status', type: 'string', visible: true },
      { field: 'createdAt', label: 'Created', type: 'date', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'createdAt', direction: 'desc' }],
    groupBy: ['source'],
    dateRange: 'this-month',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Pipeline Summary',
    description: 'Overview of deal pipeline by stage',
    dataSource: 'deals',
    columns: [
      { field: 'title', label: 'Deal', type: 'string', visible: true },
      { field: 'contactName', label: 'Contact', type: 'string', visible: true },
      { field: 'value', label: 'Value', type: 'currency', visible: true },
      { field: 'stageId', label: 'Stage', type: 'string', visible: true },
      { field: 'probability', label: 'Probability', type: 'percentage', visible: true },
      { field: 'createdAt', label: 'Created', type: 'date', visible: true },
    ],
    filters: [{ field: 'outcome', operator: 'equals', value: 'active' }],
    sortBy: [{ column: 'value', direction: 'desc' }],
    groupBy: ['stageId'],
    dateRange: 'all-time',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Activity Report',
    description: 'Track sales activities and outcomes',
    dataSource: 'activities',
    columns: [
      { field: 'title', label: 'Activity', type: 'string', visible: true },
      { field: 'type', label: 'Type', type: 'string', visible: true },
      { field: 'entityName', label: 'Related To', type: 'string', visible: true },
      { field: 'status', label: 'Status', type: 'string', visible: true },
      { field: 'scheduledAt', label: 'Scheduled', type: 'date', visible: true },
      { field: 'assignedTo', label: 'Assigned To', type: 'string', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'scheduledAt', direction: 'desc' }],
    dateRange: 'this-week',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Win/Loss Analysis',
    description: 'Analyze closed deals and their outcomes',
    dataSource: 'deals',
    columns: [
      { field: 'title', label: 'Deal', type: 'string', visible: true },
      { field: 'contactName', label: 'Contact', type: 'string', visible: true },
      { field: 'value', label: 'Value', type: 'currency', visible: true },
      { field: 'outcome', label: 'Outcome', type: 'string', visible: true },
      { field: 'source', label: 'Source', type: 'string', visible: true },
      { field: 'closedAt', label: 'Closed', type: 'date', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'closedAt', direction: 'desc' }],
    groupBy: ['outcome'],
    dateRange: 'this-quarter',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Revenue by Project Type',
    description: 'Revenue breakdown by project category',
    dataSource: 'deals',
    columns: [
      { field: 'title', label: 'Deal', type: 'string', visible: true },
      { field: 'projectType', label: 'Project Type', type: 'string', visible: true },
      { field: 'value', label: 'Value', type: 'currency', visible: true },
      { field: 'contactName', label: 'Contact', type: 'string', visible: true },
      { field: 'location', label: 'Location', type: 'string', visible: true },
    ],
    filters: [{ field: 'outcome', operator: 'equals', value: 'won' }],
    sortBy: [{ column: 'value', direction: 'desc' }],
    groupBy: ['projectType'],
    dateRange: 'this-year',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Geographic Performance',
    description: 'Deal performance by location',
    dataSource: 'deals',
    columns: [
      { field: 'location', label: 'Location', type: 'string', visible: true },
      { field: 'title', label: 'Deal', type: 'string', visible: true },
      { field: 'value', label: 'Value', type: 'currency', visible: true },
      { field: 'outcome', label: 'Outcome', type: 'string', visible: true },
      { field: 'projectType', label: 'Project Type', type: 'string', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'value', direction: 'desc' }],
    groupBy: ['location'],
    dateRange: 'this-year',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Project Status Report',
    description: 'Current status of all active projects',
    dataSource: 'projects',
    columns: [
      { field: 'name', label: 'Project', type: 'string', visible: true },
      { field: 'clientName', label: 'Client', type: 'string', visible: true },
      { field: 'status', label: 'Status', type: 'string', visible: true },
      { field: 'value', label: 'Value', type: 'currency', visible: true },
      { field: 'startDate', label: 'Start Date', type: 'date', visible: true },
      { field: 'targetEndDate', label: 'Target End', type: 'date', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'startDate', direction: 'desc' }],
    groupBy: ['status'],
    dateRange: 'all-time',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Monthly Summary',
    description: 'High-level monthly performance summary',
    dataSource: 'conversion-metrics',
    columns: [
      { field: 'source', label: 'Source', type: 'string', visible: true },
      { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
      { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
      { field: 'conversionRate', label: 'Conversion', type: 'percentage', visible: true },
      { field: 'wonCount', label: 'Won', type: 'number', visible: true },
      { field: 'totalValue', label: 'Revenue', type: 'currency', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'totalValue', direction: 'desc' }],
    dateRange: 'this-month',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  // UTM Campaign Reports
  {
    name: 'UTM Campaign Performance',
    description: 'Analyze marketing campaign effectiveness by UTM parameters',
    dataSource: 'utm-campaigns',
    columns: [
      { field: 'campaign', label: 'Campaign', type: 'string', visible: true },
      { field: 'source', label: 'Source', type: 'string', visible: true },
      { field: 'medium', label: 'Medium', type: 'string', visible: true },
      { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
      { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
      { field: 'conversionRate', label: 'Conv. Rate', type: 'percentage', visible: true },
      { field: 'totalRevenue', label: 'Revenue', type: 'currency', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'leadCount', direction: 'desc' }],
    groupBy: ['campaign'],
    dateRange: 'this-quarter',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'UTM Source Attribution',
    description: 'Track which traffic sources drive the highest quality leads',
    dataSource: 'utm-sources',
    columns: [
      { field: 'source', label: 'UTM Source', type: 'string', visible: true },
      { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
      { field: 'dealCount', label: 'Deals', type: 'number', visible: true },
      { field: 'conversionRate', label: 'Lead→Deal', type: 'percentage', visible: true },
      { field: 'winRate', label: 'Win Rate', type: 'percentage', visible: true },
      { field: 'totalRevenue', label: 'Revenue', type: 'currency', visible: true },
      { field: 'avgDealValue', label: 'Avg Deal', type: 'currency', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'totalRevenue', direction: 'desc' }],
    dateRange: 'this-year',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
  {
    name: 'Marketing ROI by Campaign',
    description: 'Compare ROI across different marketing campaigns',
    dataSource: 'utm-campaigns',
    columns: [
      { field: 'campaign', label: 'Campaign', type: 'string', visible: true },
      { field: 'leadCount', label: 'Leads', type: 'number', visible: true },
      { field: 'wonCount', label: 'Won Deals', type: 'number', visible: true },
      { field: 'winRate', label: 'Win Rate', type: 'percentage', visible: true },
      { field: 'totalRevenue', label: 'Revenue', type: 'currency', visible: true },
      { field: 'avgDealValue', label: 'Avg Deal', type: 'currency', visible: true },
    ],
    filters: [],
    sortBy: [{ column: 'totalRevenue', direction: 'desc' }],
    dateRange: 'this-year',
    createdBy: 'system',
    createdAt: new Date(),
    updatedAt: new Date(),
    isTemplate: true,
  },
];
